import { Fragment, useMemo, useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { KeyRound, Mail, Loader2, Copy, Check, X, UserPlus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { bulkLoginLinkLabel, loginLinkLabel } from "@/lib/loginLinkLabels";

type Row = {
  kind: "employer" | "employee";
  employeeId: number | null;
  userId: string | null;
  email: string | null;
  name: string;
  status:
    | "pending_activation"
    | "setup_link_sent"
    | "temp_password_issued"
    | "active";
  mustChangePassword: boolean;
  hasActiveSetupLink: boolean;
  setupLinkExpiresAt: string | null;
  lastCredentialIssuedAt: string | null;
  lastCredentialKind: "setup_link" | "temp_password" | null;
  lastLoginAt: string | null;
};

// Simplified, broker-facing buckets. We collapse the finer-grained backend
// states (setup_link_sent / temp_password_issued) into the binary "can this
// user log in or not?" The "Last credential" column carries the nuance.
type Filter = "all" | "pending_activation" | "active";

const FILTER_LABELS: Record<Filter, string> = {
  all: "All",
  pending_activation: "Pending activation",
  active: "Active",
};

function matchesFilter(row: Row, filter: Filter): boolean {
  if (filter === "all") return true;
  if (filter === "active") return row.status === "active";
  // pending_activation: everything that isn't fully active
  return row.status !== "active";
}

function statusBadge(row: Row) {
  if (row.status === "active") {
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Pending activation</Badge>;
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  return d.toLocaleDateString();
}

function lastCredentialCell(row: Row) {
  // A login newer than the last issued credential supersedes it — the person
  // has activated, so "Setup link sent" would be stale/misleading.
  if (
    row.lastLoginAt &&
    (!row.lastCredentialIssuedAt ||
      new Date(row.lastLoginAt) > new Date(row.lastCredentialIssuedAt))
  ) {
    return <span>Last login · {formatRelativeTime(row.lastLoginAt)}</span>;
  }
  if (!row.lastCredentialKind || !row.lastCredentialIssuedAt) {
    return <span className="text-gray-400">No action yet</span>;
  }
  const when = formatRelativeTime(row.lastCredentialIssuedAt);
  if (row.lastCredentialKind === "setup_link") {
    const showExpired = row.status !== "active" && !row.hasActiveSetupLink;
    return (
      <div className="flex flex-col">
        <span>Setup link sent · {when}</span>
        {showExpired && (
          <span className="text-xs text-red-600 dark:text-red-400">Link expired</span>
        )}
      </div>
    );
  }
  return <span>Temp password issued · {when}</span>;
}

export default function LoginManagement({ clientId }: { clientId: number }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmBulkMode, setConfirmBulkMode] = useState<"reset_link" | "temp_password" | null>(null);
  // Recently generated temp passwords, keyed by row key (rowKeyFor()).
  // Surfaced inline beneath each affected row until the broker dismisses them.
  const [tempPasswords, setTempPasswords] = useState<Record<string, string>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const rowKeyFor = (kind: "employer" | "employee", employeeId: number | null): string =>
    kind === "employer" ? "employer" : `employee-${employeeId}`;

  const stashTempPassword = (key: string, password: string) => {
    setTempPasswords((prev) => ({ ...prev, [key]: password }));
  };
  const dismissTempPassword = (key: string) => {
    setTempPasswords((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };
  const dismissAllTempPasswords = () => setTempPasswords({});
  const copyTempPassword = async (key: string, password: string) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((c) => (c === key ? null : c)), 1500);
    } catch {
      // ignore
    }
  };

  const { data, isLoading, refetch } = useQuery<{ rows: Row[] }>({
    queryKey: ["/api/clients", clientId, "users", "login-status"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/users/login-status`);
      if (!res.ok) throw new Error("Failed to load login status");
      return res.json();
    },
  });

  const allRows = data?.rows ?? [];
  const filteredRows = useMemo(
    () => allRows.filter((r) => matchesFilter(r, filter)),
    [allRows, filter],
  );
  const employeeRowsInFilter = filteredRows.filter(
    (r) => r.kind === "employee" && r.employeeId != null && r.email,
  );
  const allSelected =
    employeeRowsInFilter.length > 0 &&
    employeeRowsInFilter.every((r) => r.employeeId != null && selected.has(r.employeeId));

  const bulkLinkLabel = bulkLoginLinkLabel(
    allRows.filter((r) => r.employeeId != null && selected.has(r.employeeId)),
  );

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(employeeRowsInFilter.map((r) => r.employeeId!).filter(Boolean)));
    }
  };

  const toggleRow = (employeeId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  const bulkSendMutation = useMutation({
    mutationFn: async (mode: "reset_link" | "temp_password") => {
      const res = await apiRequest("POST", `/api/clients/${clientId}/users/bulk-send-login`, {
        employeeIds: Array.from(selected),
        mode,
        sendEmail: true,
      });
      const json = (await res.json()) as {
        mode: "reset_link" | "temp_password";
        successCount: number;
        failureCount: number;
        results: Array<{ employeeId: number; ok: boolean; error?: string; tempPassword?: string }>;
      };
      return json;
    },
    onSuccess: (result) => {
      if (result.mode === "temp_password") {
        for (const r of result.results) {
          if (r.ok && r.tempPassword) {
            stashTempPassword(rowKeyFor("employee", r.employeeId), r.tempPassword);
          }
        }
      }
      toast({
        title: `Sent to ${result.successCount} ${result.successCount === 1 ? "user" : "users"}`,
        description:
          result.failureCount > 0
            ? `${result.failureCount} failed — check missing emails or duplicate accounts.`
            : result.mode === "temp_password"
              ? "Temp passwords shown below each row."
              : undefined,
        variant: result.failureCount > 0 ? "destructive" : "success",
      });
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["/api/clients", clientId, "users", "login-status"] });
    },
    onError: (err: any) => {
      toast({ title: "Bulk send failed", description: err?.message, variant: "destructive" });
    },
  });

  const grantAccessMutation = useMutation({
    mutationFn: async (employeeId: number) => {
      const res = await apiRequest(
        "POST",
        `/api/employees/${employeeId}/grant-access`,
        {},
      );
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Access granted",
        description: "User account created. Send the login credential when ready.",
        variant: "success",
      });
      qc.invalidateQueries({ queryKey: ["/api/clients", clientId, "users", "login-status"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to grant access", description: err?.message, variant: "destructive" });
    },
  });

  const singleSendMutation = useMutation({
    mutationFn: async (args: {
      employeeId: number | null;
      isEmployer: boolean;
      mode: "reset_link" | "temp_password";
    }) => {
      const path = args.isEmployer
        ? `/api/clients/${clientId}/send-login`
        : `/api/employees/${args.employeeId}/send-login`;
      const res = await apiRequest("POST", path, { mode: args.mode, sendEmail: true });
      const json = await res.json();
      return { ...json, _args: args } as any;
    },
    onSuccess: (data: any) => {
      if (data?.mode === "temp_password" && data?.tempPassword) {
        const key = rowKeyFor(
          data._args.isEmployer ? "employer" : "employee",
          data._args.employeeId,
        );
        stashTempPassword(key, data.tempPassword);
        toast({ title: "Temp password generated — shown in row", variant: "success" });
      } else {
        toast({ title: "Sent", variant: "success" });
      }
      refetch();
    },
    onError: (err: any) => {
      toast({ title: "Failed", description: err?.message, variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5" />
          Login Management
        </CardTitle>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Send activation or password reset links, or generate temporary passwords for this client's users.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {(Object.keys(FILTER_LABELS) as Filter[]).map((f) => {
            const count = allRows.filter((r) => matchesFilter(r, f)).length;
            return (
              <Button
                key={f}
                variant={filter === f ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(f)}
              >
                {FILTER_LABELS[f]} <span className="ml-1.5 text-xs opacity-70">{count}</span>
              </Button>
            );
          })}
        </div>

        {selected.size > 0 && (
          <div className="flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40 p-3">
            <div className="text-sm">
              <strong>{selected.size}</strong> selected
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => setConfirmBulkMode("reset_link")}
                disabled={bulkSendMutation.isPending}
              >
                <Mail className="mr-1.5 h-3.5 w-3.5" />
                {bulkLinkLabel}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmBulkMode("temp_password")}
                disabled={bulkSendMutation.isPending}
              >
                Generate temp passwords
              </Button>
            </div>
          </div>
        )}

        {Object.keys(tempPasswords).length > 0 && (
          <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40 p-2 text-xs">
            <span className="text-amber-900 dark:text-amber-200">
              Showing {Object.keys(tempPasswords).length} temporary password
              {Object.keys(tempPasswords).length === 1 ? "" : "s"} inline. These won't reappear once dismissed — copy what you need first.
            </span>
            <Button size="sm" variant="ghost" className="h-7" onClick={dismissAllTempPasswords}>
              Clear all
            </Button>
          </div>
        )}

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    disabled={employeeRowsInFilter.length === 0}
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last credential</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Loading…
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-sm text-gray-500">
                    No users match this filter.
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row) => {
                  const isEmployer = row.kind === "employer";
                  const checkable = !isEmployer && row.employeeId != null && !!row.email;
                  const rowKey = rowKeyFor(row.kind, row.employeeId);
                  const inlineTempPassword = tempPasswords[rowKey];
                  return (
                    <Fragment key={`${row.kind}-${row.employeeId ?? row.userId ?? row.email}`}>
                      <TableRow>
                        <TableCell>
                          {checkable ? (
                            <Checkbox
                              checked={row.employeeId != null && selected.has(row.employeeId)}
                              onCheckedChange={() => row.employeeId != null && toggleRow(row.employeeId)}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell className="font-medium">
                          {row.employeeId != null ? (
                            <Link
                              href={`/clients/${clientId}/employees/${row.employeeId}`}
                              className="text-blue-600 hover:underline"
                            >
                              {row.name}
                            </Link>
                          ) : (
                            row.name
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {row.email || <span className="italic text-gray-400">No email</span>}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{isEmployer ? "Employer (HR)" : "Employee"}</Badge>
                        </TableCell>
                        <TableCell>{statusBadge(row)}</TableCell>
                        <TableCell
                          className="text-sm text-gray-600 dark:text-gray-400"
                          title={row.lastCredentialIssuedAt ?? undefined}
                        >
                          {lastCredentialCell(row)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {!isEmployer && !row.userId && row.email && row.employeeId != null && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={grantAccessMutation.isPending}
                                onClick={() => grantAccessMutation.mutate(row.employeeId!)}
                                title="Create a user account for this employee. No email is sent — use Send login afterwards."
                              >
                                <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                                Grant access
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={!row.email || singleSendMutation.isPending}
                                >
                                  Send login
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onSelect={() =>
                                    singleSendMutation.mutate({
                                      employeeId: row.employeeId,
                                      isEmployer,
                                      mode: "reset_link",
                                    })
                                  }
                                >
                                  {loginLinkLabel(row)}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onSelect={() =>
                                    singleSendMutation.mutate({
                                      employeeId: row.employeeId,
                                      isEmployer,
                                      mode: "temp_password",
                                    })
                                  }
                                >
                                  Generate temporary password
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                      {inlineTempPassword && (
                        <TableRow className="bg-amber-50/60 dark:bg-amber-950/30 border-t-0">
                          <TableCell />
                          <TableCell colSpan={5}>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              <span className="text-xs font-medium text-amber-800 dark:text-amber-200">
                                Temporary password:
                              </span>
                              <code className="rounded bg-white dark:bg-gray-900 border px-2 py-0.5 font-mono text-sm break-all">
                                {inlineTempPassword}
                              </code>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1"
                                onClick={() => copyTempPassword(rowKey, inlineTempPassword)}
                              >
                                {copiedKey === rowKey ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                {copiedKey === rowKey ? "Copied" : "Copy"}
                              </Button>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Share securely. User must change it on first login.
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => dismissTempPassword(rowKey)}
                              aria-label="Dismiss"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <AlertDialog
        open={confirmBulkMode !== null}
        onOpenChange={(open) => !open && setConfirmBulkMode(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmBulkMode === "reset_link"
                ? `${bulkLinkLabel} to ${selected.size} ${selected.size === 1 ? "user" : "users"}?`
                : `Generate temporary passwords for ${selected.size} users?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmBulkMode === "reset_link"
                ? "Each user will receive a setup link, expiring in 7 days. Any existing setup link for these users will be invalidated."
                : "A temporary password will be generated and emailed to each user. Any existing password and pending setup link will be invalidated."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const mode = confirmBulkMode!;
                setConfirmBulkMode(null);
                bulkSendMutation.mutate(mode);
              }}
            >
              {bulkSendMutation.isPending ? "Sending…" : "Send"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
